/**
 * StatusUpdateDialog — Reusable unified status update dialog.
 *
 * Used in: LeadWizard, Claims Sent tab, anywhere status updates are needed.
 * Calls PUT /api/leads/{id}/processing-status
 *
 * Props:
 *   leadId        — lead ID to update
 *   currentStatus — current lead_status value (for display)
 *   visible       — dialog visibility
 *   onHide        — close handler
 *   onSuccess     — callback after successful update
 *   preselect     — optional: pre-select a status (e.g., 'processor_rejected')
 *   toastRef      — optional: external toast ref (if not provided, uses internal)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import axiosInstance from '../../config/axios.config';
import { getProcessingStatusConfig, REJECTION_REASONS, PROCESSOR_REJECTION_REASONS } from '../../services/leadProcessingService';

const STATUS_OPTIONS = [
    { label: 'Broadcasted', value: 'broadcasted' },
    { label: 'Processing', value: 'processing' },
    { label: 'Bank Submit', value: 'bank_submit' },
    { label: 'Bank Queries', value: 'bank_queries' },
    { label: 'Bank Processing', value: 'bank_processing' },
    { label: 'Sanction', value: 'sanction' },
    { label: 'Sanction Queries', value: 'sanction_queries' },
    { label: 'Disbursal Queries', value: 'disbursal_queries' },
    { label: 'Disbursal', value: 'disbursal' },
    { label: 'Bank Rejected', value: 'bank_rejected' },
    { label: 'Customer Rejected', value: 'customer_rejected' },
    { label: 'Processor Rejected', value: 'processor_rejected' },
    { label: 'Closed', value: 'closed' },
];

const REJECTION_STATUSES = ['bank_rejected', 'customer_rejected', 'processor_rejected'];

const getRejectionReasons = (status) => {
    if (status === 'bank_rejected') return REJECTION_REASONS.map(r => ({ label: r.label, value: r.code }));
    if (status === 'processor_rejected') return PROCESSOR_REJECTION_REASONS.map(r => ({ label: r.label, value: r.code }));
    if (status === 'customer_rejected') return [
        { label: 'Customer Not Interested', value: 'not_interested' },
        { label: 'Found Better Rate Elsewhere', value: 'found_better_rate' },
        { label: 'Loan No Longer Needed', value: 'no_longer_needed' },
        { label: 'Terms Not Acceptable', value: 'terms_not_acceptable' },
        { label: 'Customer Unreachable', value: 'unreachable' },
        { label: 'Other', value: 'other_customer' },
    ];
    return [];
};

/**
 * Props:
 *   leadId          — lead ID to update
 *   currentStatus   — current lead_status value
 *   visible         — dialog visibility
 *   onHide          — close handler
 *   onSuccess       — callback after successful update
 *   preselect       — optional: pre-select a status
 *   toastRef        — optional: external toast ref
 *   defaultBankId   — optional: auto-fill bank ID (from offer context)
 *   defaultBankName — optional: auto-fill bank name (from offer context)
 *   offerBanks      — optional: array of {id, name} from lead's offers for dropdown
 */
const StatusUpdateDialog = ({ leadId, currentStatus, visible, onHide, onSuccess, preselect, toastRef, defaultBankId, defaultBankName, offerBanks }) => {
    const internalToast = useRef(null);
    const toast = toastRef || internalToast;

    const [status, setStatus] = useState('');
    const [rejectionReasonCode, setRejectionReasonCode] = useState(null);
    const [rejectionDetail, setRejectionDetail] = useState('');
    const [bankId, setBankId] = useState(null);
    const [bankName, setBankName] = useState('');
    const [notes, setNotes] = useState('');
    const [updating, setUpdating] = useState(false);

    // Reset form when dialog opens — auto-fill bank from context
    useEffect(() => {
        if (visible) {
            setStatus(preselect || '');
            setRejectionReasonCode(null);
            setRejectionDetail('');
            setBankId(defaultBankId || null);
            setBankName(defaultBankName || '');
            setNotes('');
        }
    }, [visible, preselect, defaultBankId, defaultBankName]);

    const isRejection = REJECTION_STATUSES.includes(status);

    const handleSubmit = async () => {
        if (!status) return;
        if (isRejection && !rejectionReasonCode) {
            toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Select rejection reason', life: 3000 });
            return;
        }

        setUpdating(true);
        try {
            const payload = { status, notes: notes || undefined };
            if (isRejection) {
                payload.rejection_reason_code = rejectionReasonCode || undefined;
                const reasons = getRejectionReasons(status);
                payload.rejection_reason_label = reasons.find(r => r.value === rejectionReasonCode)?.label || undefined;
                payload.rejection_detail = rejectionDetail || undefined;
            }
            if (bankId) payload.bank_id = bankId;
            if (bankName) payload.bank_name = bankName;

            const res = await axiosInstance.put(`/leads/${leadId}/processing-status`, payload);
            if (res.data.success) {
                toast.current?.show({ severity: 'success', summary: 'Updated', detail: res.data.message || 'Status updated', life: 3000 });
                onHide();
                onSuccess?.();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
            }
        } catch (e) {
            const msg = e.response?.data?.message || 'Failed to update status';
            toast.current?.show({ severity: e.response?.status === 403 ? 'warn' : 'error', summary: e.response?.status === 403 ? 'Access Denied' : 'Error', detail: msg, life: 4000 });
        } finally {
            setUpdating(false);
        }
    };

    const currentConfig = getProcessingStatusConfig(currentStatus);

    return (
        <>
            {!toastRef && <Toast ref={internalToast} />}
            <Dialog
                header="Update Processing Status"
                visible={visible}
                style={{ width: '90vw', maxWidth: '460px' }}
                modal
                onHide={onHide}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={updating} />
                        <Button label="Update" icon="pi pi-check" onClick={handleSubmit} loading={updating} disabled={!status} />
                    </div>
                }
            >
                {/* Current status */}
                {currentStatus && (
                    <div className="flex align-items-center gap-2 mb-3 p-2 surface-50 border-round">
                        <span className="text-500" style={{ fontSize: '0.75rem' }}>Current:</span>
                        <Tag value={currentConfig.label} severity={currentConfig.severity} icon={`pi ${currentConfig.icon}`} className="text-xs" />
                    </div>
                )}

                {/* Status dropdown */}
                <div className="field mb-3">
                    <label className="block text-600 font-semibold mb-1" style={{ fontSize: '0.8rem' }}>New Status</label>
                    <Dropdown
                        value={status}
                        options={STATUS_OPTIONS}
                        onChange={(e) => { setStatus(e.value); setRejectionReasonCode(null); }}
                        placeholder="Select status"
                        className="w-full"
                    />
                </div>

                {/* Rejection form */}
                {isRejection && (
                    <div className="p-3 border-round mb-3" style={{ background: 'var(--red-50)', border: '1px solid var(--red-200)' }}>
                        <div className="text-red-700 font-semibold mb-2" style={{ fontSize: '0.8rem' }}>
                            <i className="pi pi-exclamation-triangle mr-1" />
                            Rejection Details
                        </div>
                        <div className="field mb-2">
                            <label className="block text-600 mb-1" style={{ fontSize: '0.75rem' }}>Reason *</label>
                            <Dropdown
                                value={rejectionReasonCode}
                                options={getRejectionReasons(status)}
                                onChange={(e) => setRejectionReasonCode(e.value)}
                                placeholder="Select reason"
                                className="w-full"
                            />
                        </div>
                        <div className="field">
                            <label className="block text-600 mb-1" style={{ fontSize: '0.75rem' }}>Detail</label>
                            <InputTextarea
                                value={rejectionDetail}
                                onChange={(e) => setRejectionDetail(e.target.value)}
                                placeholder="Describe the rejection..."
                                rows={2}
                                className="w-full"
                            />
                        </div>
                    </div>
                )}

                {/* Bank selection (for bank-related statuses) */}
                {['bank_submit', 'bank_queries', 'bank_processing', 'sanction', 'sanction_queries', 'disbursal_queries', 'disbursal', 'bank_rejected'].includes(status) && (
                    <div className="field mb-3">
                        <label className="block text-600 mb-1" style={{ fontSize: '0.75rem' }}>Bank</label>
                        {offerBanks && offerBanks.length > 0 ? (
                            <Dropdown
                                value={bankId}
                                options={offerBanks.map(b => ({ label: b.name, value: b.id }))}
                                onChange={(e) => {
                                    setBankId(e.value);
                                    const selected = offerBanks.find(b => b.id === e.value);
                                    setBankName(selected?.name || '');
                                }}
                                placeholder="Select bank..."
                                className="w-full"
                                showClear
                            />
                        ) : (
                            <input
                                type="text"
                                value={bankName}
                                onChange={(e) => { setBankName(e.target.value); setBankId(null); }}
                                placeholder="Enter bank name..."
                                className="p-inputtext p-component w-full"
                                style={{ fontSize: '0.85rem' }}
                            />
                        )}
                        {bankName && (
                            <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>Selected: {bankName}</div>
                        )}
                    </div>
                )}

                {/* Notes */}
                <div className="field">
                    <label className="block text-600 mb-1" style={{ fontSize: '0.75rem' }}>Notes (optional)</label>
                    <InputTextarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any additional notes..."
                        rows={2}
                        className="w-full"
                    />
                </div>
            </Dialog>
        </>
    );
};

export default StatusUpdateDialog;
